import type { ConverseEvent } from './converse-types';

export type PendingRoleforgeApproval = {
  message: string;
  actionKey: string;
  resumeToken: string;
  details: Record<string, unknown>;
};

export function createRoleforgeActionKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `roleforge-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function pendingRoleforgeApprovalFromEvent(
  message: string,
  event: ConverseEvent,
): PendingRoleforgeApproval | null {
  if (event.type !== 'error' || event.code !== 'credit_spend_review_required') return null;
  const actionKey = typeof event.actionKey === 'string' ? event.actionKey : '';
  const resumeToken = typeof event.details?.resumeToken === 'string' ? event.details.resumeToken : '';
  if (!actionKey || !resumeToken) return null;
  return { message, actionKey, resumeToken, details: event.details ?? {} };
}

export function isTerminalRoleforgeApprovalEvent(event: ConverseEvent): boolean {
  if (event.type !== 'error') return false;
  if (
    event.code === 'credit_spend_rejected'
    || event.code === 'credit_spend_approval_expired'
    || event.code === 'invalid_resume_token'
  ) {
    return true;
  }
  const message = event.message.toLowerCase().replaceAll('_', ' ');
  return message.includes('resume token')
    && (message.includes('invalid') || message.includes('expired'));
}

export function approvalEstimateCopy(details: Record<string, unknown>): string | null {
  const estimated = details.estimatedCredits;
  return typeof estimated === 'number' && Number.isFinite(estimated)
    ? `Estimated hosted spend: ${estimated.toLocaleString()} credits.`
    : null;
}

export function loadPendingRoleforgeApproval(scopeKey: string): PendingRoleforgeApproval | null {
  const storage = (globalThis as { sessionStorage?: Storage }).sessionStorage;
  if (!storage) return null;
  try {
    const parsed = JSON.parse(storage.getItem(`roleforge:hosted-credit:${scopeKey}`) ?? 'null');
    return parsed && typeof parsed.message === 'string' && typeof parsed.actionKey === 'string'
      && typeof parsed.resumeToken === 'string' && parsed.details && typeof parsed.details === 'object'
      ? parsed as PendingRoleforgeApproval
      : null;
  } catch {
    return null;
  }
}

export function savePendingRoleforgeApproval(scopeKey: string, pending: PendingRoleforgeApproval | null): void {
  const storage = (globalThis as { sessionStorage?: Storage }).sessionStorage;
  if (!storage) return;
  const key = `roleforge:hosted-credit:${scopeKey}`;
  try {
    if (pending) storage.setItem(key, JSON.stringify(pending));
    else storage.removeItem(key);
  } catch {
    // Keep the in-memory continuation usable if storage is unavailable.
  }
}
